'use client';

import { cn } from '@/utils';
import type { ClassValue } from 'clsx'; // ClassValue 타입 import
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React, { ReactNode } from 'react';

type CasingOption = 'upper' | 'lower' | 'upper-each' | 'lower-each';

type BreadcrumbProps = {
  homeElement?: ReactNode;
  separator?: ReactNode;
  containerClassName?: ClassValue;
  listClassName?: ClassValue;
  activeClassName?: ClassValue;
  linkClassName?: ClassValue;
  replaceLastTo?: string;
  forceReplaceLastTo?: boolean;
  casing?: CasingOption;
};

function handleLinkText(text: string, casing?: CasingOption): string {
  if (!casing || !text) return text;

  text = text.trim();
  if (!text) return '';

  switch (casing) {
    case 'upper':
      return text.toUpperCase();
    case 'lower':
      return text.toLowerCase();
    case 'upper-each':
    case 'lower-each': {
      const words = text.split(/[\s-]+/);
      const mapper = casing === 'upper-each' ? (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : (s: string) => s.toLowerCase();
      return words.map((word) => (word ? mapper(word) : '')).join(' ');
    }
    default:
      return text;
  }
}

export function Breadcrumb({
  homeElement = 'home',
  separator = <span className="mx-1 text-gray-400 dark:text-gray-600">/</span>,
  containerClassName,
  listClassName,
  activeClassName,
  linkClassName,
  casing = 'lower-each',
  replaceLastTo = 'current',
  forceReplaceLastTo = true,
}: BreadcrumbProps) {
  const pathname = usePathname();
  const pathSegments = pathname.split('/').filter(Boolean).map(decodeURIComponent);

  const defaultLinkClasses = 'transition-colors hover:text-primary dark:hover:text-primary-dark';
  const defaultActiveClasses = 'font-semibold text-foreground dark:text-foreground-dark'; // 기본 활성 스타일

  return (
    <nav aria-label="Breadcrumb">
      <ol className={cn('flex flex-wrap items-center gap-x-1 text-sm text-muted-foreground dark:text-muted-foreground-dark', containerClassName)}>
        <li className={cn('inline-flex items-center font-mono', listClassName)}>
          <Link href="/" className={cn(defaultLinkClasses, linkClassName, pathname === '/' ? cn(defaultActiveClasses, activeClassName) : '')}>
            {homeElement}
          </Link>
        </li>

        {pathSegments.map((segment, index) => {
          const href = `/${pathSegments.slice(0, index + 1).join('/')}`;
          const isLast = index === pathSegments.length - 1;
          const isActive = pathname === href;

          let displayText = isLast && replaceLastTo ? replaceLastTo : segment;
          displayText = handleLinkText(displayText, casing);

          return (
            <React.Fragment key={href}>
              {' '}
              <li className="inline-flex items-center" aria-hidden="true">
                {separator}
              </li>
              <li className={cn(`inline-flex items-center font-mono`, listClassName)}>
                {!isLast || forceReplaceLastTo ? (
                  <Link
                    href={href}
                    className={cn(defaultLinkClasses, linkClassName, isActive ? cn(defaultActiveClasses, activeClassName) : '')}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {displayText}
                  </Link>
                ) : (
                  <span className={cn(defaultActiveClasses, activeClassName)} aria-current="page">
                    {displayText}
                  </span>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
